import { Grid, Typography } from '@material-ui/core';
import useScrollButtonState from '@ncr-retail/hooks/build/useScrollButtonState/useScrollButtonState';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { CartMetadata } from 'src/common/interfaces/dtos/ICartMetadata';
import { IButton } from 'src/common/interfaces/IButton';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { ReactComponent as Calculator } from 'src/common/media/Calculator.svg';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import Pagination from 'src/pagination/pagination';
import { getRecallOrder } from 'src/search-order/business-logic/get-recall-order';
import { recallOrderByCodeEntered } from 'src/search-order/business-logic/recall-order';
import OrderItemList from './order-item-list';
import { styles } from './recall-order-display-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';

type Props = IRecallOrderDisplayState;

const RecallOrderDisplay: React.FC<Props> = ({
  leftHanded,
  orders,
  trainingModeActive,
}: Props): JSX.Element => {
  const classes = styles({ leftHanded });
  const { t } = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState<string>('');
  const [isKeypadVisible, setKeypadVisibility] = useState<boolean>(true);
  const [isPhoneNumber, setIsPhoneNumber] = useState<boolean>(false);
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();

  const RECALL_ORDER_INPUT_INSTRUCTIONS = t('RecallHelperText');
  const RECALL_TITLE = t('RecallOrder_Command');
  const RECALL_BY_PHONE = t('SearchRecallOrderView_PhoneNumberCode');
  const RECALL_BY_LOYALTY = t('R10OrderGetView_ScanOrderIdOrCustomerId');
  const RECALL_BY_PHONE_BUTTON = t('RecallByPhone');
  const RECALL_BY_LOYALTY_BUTTON = t('RecallByLoyalty');
  const RECALL_BUTTON = t('Recall');

  const hasOrders = orders?.length > 0;
  const flexDirection = leftHanded ? 'row-reverse' : 'row';

  const onSelectOrder = (orderId?: string): void => {
    if (orderId) {
      setSelectedOrder(orderId);
    }
  };

  const onSwitch = (): void => {
    setIsPhoneNumber(!isPhoneNumber);
  };

  const onKeypadSubmit = (input: string): void => {
    const itemData: IItemInputData = {
      itemId: input,
      entryMethod: EntryMethod.Keyed,
    };
    recallOrderByCodeEntered(itemData, isPhoneNumber).catch(popupMessage);
  };

  const onRecallOrder = (): void => {
    getRecallOrder(selectedOrder)
      .then(() => {
        setSelectedOrder('');
      })
      .catch(popupMessage);
  };

  const toggleKeyPad = (): void => {
    setKeypadVisibility(!isKeypadVisible);
  };

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: onKeypadSubmit,
  };

  useEffect(() => {
    if (orders.length === 0) {
      setIsPhoneNumber(false);
    }
    setSelectedOrder('');
  }, [orders]);

  const trainingModeClasses = trainingModeStyles();

  return (
    <Grid
      container
      className={`${classes.background} fullHeight ${
        trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
      }`}
      wrap='nowrap'
      direction={flexDirection}
    >
      {trainingModeActive && <TrainingModeWatermark />}
      <Grid item className={classes.displayBody}>
        <Grid container direction='column' className='fullHeight' wrap='nowrap'>
          <Grid item>
            <Typography className={classes.recallOrderLabel}>{RECALL_TITLE}</Typography>
          </Grid>
          <Grid item className={`fullWidth ${classes.listScrollWrapper}`}>
            <div ref={scrollRef} className='verticalScroll fullWidth fullHeight'>
              <OrderItemList
                orders={orders}
                isPhoneNumber={isPhoneNumber}
                selectedOrder={selectedOrder}
                onSelectOrder={onSelectOrder}
              />
            </div>
          </Grid>
          <Grid item className={classes.bottomContainer}>
            <Grid
              container
              className='fullHeight'
              direction={flexDirection}
              wrap='nowrap'
              alignItems='center'
              justifyContent='center'
            >
              <Grid item container wrap='nowrap'>
                {hasOrders && (
                  <Grid item className={classes.paginationContainer}>
                    <Grid container wrap='nowrap'>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={lastPage}
                        updateCurrentPageHandler={onPageChanged}
                        testIdPrefix={'recall-order'}
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid item style={{ margin: '0 auto' }}>
                  <BaseButton
                    data-testid='switch-search-button'
                    className={classes.switchButton}
                    onClick={onSwitch}
                  >
                    <span className='label'>
                      {isPhoneNumber ? RECALL_BY_LOYALTY_BUTTON : RECALL_BY_PHONE_BUTTON}
                    </span>
                  </BaseButton>
                </Grid>
                {hasOrders && (
                  <Grid item>
                    <BaseButton
                      data-testid='recall-order-button'
                      className={classes.recallButton}
                      onClick={onRecallOrder}
                      disabled={selectedOrder === ''}
                    >
                      <span className='label'>{RECALL_BUTTON}</span>
                    </BaseButton>
                  </Grid>
                )}
              </Grid>
              {!isKeypadVisible && (
                <Grid item>
                  <BaseButton
                    isSquare
                    data-testid='show-keypad-button'
                    className={classes.keypadButton}
                    onClick={toggleKeyPad}
                  >
                    <Calculator />
                  </BaseButton>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isKeypadVisible && (
        <Grid item className={classes.keypadContainer}>
          <Keypad
            keyboardId={'order-recall'}
            visible={isKeypadVisible}
            topLabelText={isPhoneNumber ? RECALL_BY_PHONE : RECALL_BY_LOYALTY}
            inputInstructionsLabelText={RECALL_ORDER_INPUT_INSTRUCTIONS}
            handleClose={toggleKeyPad}
            submitButton={keypadSubmitButton}
            keypadTestIdPrefix='recall-order-display-'
            disableManualWeight
            disableQuantity
          />
        </Grid>
      )}
    </Grid>
  );
};

interface IRecallOrderDisplayState {
  orders: CartMetadata[];
  leftHanded?: boolean;
  trainingModeActive: boolean;
}

const mapStateToProps = (state: AppState): IRecallOrderDisplayState => ({
  orders: state.recallOrder.recallOrderData,
  leftHanded: state.cashierPreferences.leftHanded,
  trainingModeActive: state.posMode.trainingMode,
});

export default connect(mapStateToProps)(RecallOrderDisplay);
